/**
 * Compact circular gauge — used in the sidebar to surface CPU/RAM load at a
 * glance. Color tracks thresholds: green ≤75, amber ≤90, red >90.
 *
 * Renders as an SVG so it scales sharply at 14–18px without blurring.
 */
interface Props {
  /** Percentage 0–100. Values outside that range are clamped. */
  value: number;
  size?: number;
  /** Thickness of the arc, in pixels at the rendered size. */
  stroke?: number;
  /** Optional label override; defaults to floor(value)+'%'. */
  label?: string;
  /** When true, omits the inner number — useful in cramped rows. */
  silent?: boolean;
  className?: string;
  title?: string;
}

export default function RingGauge({
  value,
  size = 18,
  stroke = 2.5,
  label,
  silent,
  className = '',
  title,
}: Props) {
  const v = Math.max(0, Math.min(100, value));
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const dash = (v / 100) * c;
  const color =
    v > 90 ? '#f56565'
      : v > 75 ? '#f5b544'
      : '#34d399';
  const text = label ?? `${Math.round(v)}%`;

  return (
    <span
      className={`inline-flex items-center justify-center relative shrink-0 ${className}`}
      style={{ width: size, height: size }}
      title={title ?? `${Math.round(v)}%`}
      aria-label={title ?? `${Math.round(v)}%`}
      role="img"
    >
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          stroke="#262b3a"
          strokeWidth={stroke}
          fill="none"
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          stroke={color}
          strokeWidth={stroke}
          fill="none"
          strokeLinecap="round"
          strokeDasharray={`${dash} ${c}`}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
          style={{ transition: 'stroke-dasharray 400ms ease, stroke 200ms ease' }}
        />
      </svg>
      {!silent && size >= 28 && (
        <span
          className="absolute inset-0 flex items-center justify-center text-[9px] font-semibold tabular-nums"
          style={{ color }}
        >
          {text}
        </span>
      )}
    </span>
  );
}
